import { useCallback, useEffect, useState, useRef, useLayoutEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import VideoModal from '../common/VideoModal'
import SimpleButton from '../common/SimpleButton'
import SearchWithFilter from '../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import GenericTable from '../common/GenericTable'
import { toast } from '../common/Toast'
import { useAppContext } from '../../libs/contextLib'
import { useWindowSize } from '../../libs/hooks'
import { handleSetQueryParams } from '../../actions/queryParams'
import { loadVideoModels, loadVideoCategories, loadVideoLanguages } from '../../actions/videos'
import { FilledFavIcon, InformationIcon, UnFilledFavIcon, VideoPlayIcon } from '../../icons'
import { getVideosList, postVideoFavourites, putVideoFavourites } from '../../utils/requests/videosAPI'
import { checkPermission, videosWritePermission } from '../../utils/permissionValidation'
import { keyboardEvents } from '../../utils/keyboardEvents'
import { capitalizeWords } from '../../utils/functions'
import { gaActions, gaCategories, trackEvent } from '../../utils/ga4'
import { formatSnakeCase, buildSnakeLabels, getApiValueFromLabel } from '../../utils/filters/filters'

function VideosTable(props) {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { isAuthenticated } = useAppContext()
  const isMobile = useWindowSize()[0] <= 768
  const userId = props.authedUser?.cognitoSub
  const pageSize = parseInt(process.env.REACT_APP_PAGINATION_SIZE) || 10
  const [models, setModels] = useState([])
  const [videos, setVideos] = useState([])
  const allVideosRef = useRef([])
  const [videoCategories, setVideoCategories] = useState([])
  const [languages, setLanguages] = useState([])
  const [expandedVideo, setExpandedVideo] = useState(0)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedModel, setSelectedModel] = useState(props.model || null)
  const [selectedFilterLanguages, _setSelectedFilterLanguages] = useState([])
  const selectedFilterLanguagesRef = useRef([])
  const setSelectedFilterLanguages = (langs) => {
    selectedFilterLanguagesRef.current = langs
    _setSelectedFilterLanguages(langs)
  }
  const [isSearchDirty, setIsSearchDirty] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const ignoreNextVidOpenRef = useRef(false)
  const [queryParamsState, setQueryParamsState] = useState(() => ({
    orderBy: 'title',
    order: 'asc',
    ...(selectedModel ? { model: selectedModel } : {}),
  }))

  const fetcher = getVideosList
  const categoryLabels = buildSnakeLabels(videoCategories)

  const filterVideosByLanguage = (videosList, languages) => {
    if (!languages || languages.length === 0) return videosList
    const lowerLangs = languages.map((l) => l.toLowerCase())
    const filtered = videosList.filter((video) => lowerLangs.includes(video.language_code?.toLowerCase()))

    const primaryLang = lowerLangs[0]
    filtered.sort((a, b) => {
      const aIsPrimary = a.language_code?.toLowerCase() === primaryLang ? 0 : 1
      const bIsPrimary = b.language_code?.toLowerCase() === primaryLang ? 0 : 1
      return aIsPrimary - bIsPrimary
    })
    return filtered
  }

  const updateVideos = (newVideos) => {
    allVideosRef.current = newVideos || []
    setVideos(filterVideosByLanguage(allVideosRef.current, selectedFilterLanguagesRef.current))
  }

  useEffect(() => {
    if (!props.hideBreadcrumb) {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.videosPage,
        label: props.isFavourite ? 'Videos Page: Favorites Tab View' : 'Videos Page: All Videos Tab View',
      })
    }
  }, [])

  useEffect(() => {
    if (props.videoModels.length === 0) {
      props.actions.loadVideoModels()
    }

    if (props.videoCategories.length === 0) {
      props.actions.loadVideoCategories()
    }

    if (props.videosLanguages.length === 0) {
      props.actions.loadVideoLanguages()
    }
  }, [props.actions])

  useEffect(() => {
    const sortedLanguageCodes = props.videosLanguages
      .map((lang) => ({
        code: `${lang}-lang`,
        name: t([`filter_context.${lang}-lang`, lang]),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item) => item.code)

    setLanguages(sortedLanguageCodes)
  }, [props.videosLanguages])

  useEffect(() => {
    if (props.videoModels.length > 0) {
      setModels(props.videoModels)
    }
  }, [props.videoModels])

  useEffect(() => {
    if (props.videoCategories.length > 0) {
      setVideoCategories(props.videoCategories)
    }
  }, [props.videoCategories])

  useLayoutEffect(() => {
    if (props.hideFiltering) {
      return
    }

    const availableLanguages = props.videosLanguages.map((lang) => lang?.toLowerCase()).filter(Boolean)
    const userLanguage = i18n.language?.split('-')[0]?.toLowerCase() || null

    if (availableLanguages.length === 0) {
      setSelectedFilterLanguages(userLanguage ? [userLanguage] : [])
      return
    }

    if (userLanguage?.toLowerCase() === 'en') {
      setSelectedFilterLanguages(availableLanguages.includes('en') ? ['en'] : [])
      return
    }

    const selected = []
    if (userLanguage && availableLanguages.includes(userLanguage)) {
      selected.push(userLanguage)
    }
    if (availableLanguages.includes('en')) {
      selected.push('en')
    }
    setSelectedFilterLanguages(selected.length > 0 ? selected : [availableLanguages[0]])
  }, [i18n.language, props.hideFiltering, props.videosLanguages])

  useEffect(() => {
    setVideos(filterVideosByLanguage(allVideosRef.current, selectedFilterLanguages))
  }, [selectedFilterLanguages])

  const fetchVideos = useCallback(
    async (queryParams, videos) => {
      try {
        if (!userId) {
          return []
        }

        const page = await fetcher({ ...queryParams })
        //const totalPages = page?.totalPages || 1
        setCurrentPage(1)
        //setCanLoadMore(totalPages > queryParams.page)

        // const dataWithoutDuplicates = page?.data.reduce((acc, current) => {
        //   const existingVideo = acc.find((item) => item.video_id === current.video_id)
        //   if (!existingVideo) {
        //     return acc.concat([current])
        //   } else {
        //     return acc.map((item) =>
        //       item.video_id === current.video_id
        //         ? { ...item, model: `${item.model}, ${current.model}`, is_favourite: item.is_favourite || current.is_favourite }
        //         : item,
        //     )
        //   }
        // }, [])
        let allVideos = page?.data || []

        if (props.isFavourite) {
          return allVideos.filter((video) => video.is_favourite)
        }

        return allVideos
        // if (queryParams.page === 1) {
        //   return page?.data || []
        // }

        // const newVideos = videos.concat(page?.data)
        // return newVideos
      } catch (e) {
        console.error(e.message)

        toast.error(t('error.failure_msg'))
        setShowSpinner(false)
      } finally {
        setIsFirstLoad(false)
      }
    },
    [fetcher, t],
  )

  const videosTableData = (videos ?? []).map((video) => {
    const groupModelsByDevice = (models) => {
      let modelsByDevice = ''

      const uniqueProductsWithModels = []

      models.forEach((model) => {
        const splitModel = model.split(' ')
        const product = splitModel[0]
        const technology = product !== 'STATCLAVE' ? splitModel[splitModel.length - 1] : splitModel[1]

        const existingProduct = uniqueProductsWithModels.find((item) => item.product === product && item.technology === technology)
        if (!existingProduct) {
          uniqueProductsWithModels.push({ product, technology, models: [product !== 'STATCLAVE' ? splitModel[1] : []] })
        } else {
          if (product !== 'STATCLAVE') {
            existingProduct.models.push(splitModel[1])
          }
        }
      })

      uniqueProductsWithModels.forEach((item) => {
        const modelsString = item.models.join('/')
        modelsByDevice += `${item.product} ${modelsString} ${item.technology}, `
      })

      modelsByDevice = modelsByDevice.slice(0, -2)

      return modelsByDevice
    }

    const videoModels = video.models?.split(',') || []
    return {
      identifier: video.id,
      title: video.title,
      model: videoModels.length > 1 ? groupModelsByDevice(videoModels) : videoModels,
      category:
        video.category
          ?.split(',')
          ?.map((cat) => capitalizeWords(cat.replaceAll('-', ' ')))
          ?.join(', ') || '',
      isFavourite: video.is_favourite,
      favouriteId: video.favourite_id,
      thumbnailUrl: video.thumbnail_url,
      video_id: video.video_id,
    }
  })

  const openVideoModal = useCallback(
    (video) => {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.videosPage,
        label: `Videos Page: Video Selected - ${video.title}`,
      })

      setSelectedVideo(video)
      const params = new URLSearchParams(location.search)
      params.set('vid', video.video_id)
      if (!selectedModel) {
        history.replace({
          pathname: location.pathname,
          search: `?${params.toString()}`,
          state: location.state,
        })
      }
    },
    [history, location, selectedModel],
  )

  const closeVideoModal = useCallback(() => {
    setSelectedVideo(null)
    const params = new URLSearchParams(location.search)
    ignoreNextVidOpenRef.current = true
    params.delete('vid')
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
      state: location.state,
    })

    if (props.queryParams?.vid) {
      props.actions.handleSetQueryParams({ vid: null })
    }
  }, [history, location, props.actions, props.queryParams?.vid])

  useEffect(() => {
    async function loadVideoModal() {
      if (!isAuthenticated) {
        history.push({ pathname: '/' })
      }

      if (props.queryParams?.vid) {
        props.actions.handleSetQueryParams({ vid: null })
      }

      const params = new URLSearchParams(location.search)
      const vid = params.get('vid')

      if (ignoreNextVidOpenRef.current) {
        ignoreNextVidOpenRef.current = false
        return
      }

      if ((videos && videos.length === 0) || !vid) {
        return
      }

      const response = await getVideosList({
        vid,
      })
      const requestedVideo = response.data[0]

      if (requestedVideo) {
        setSelectedVideo(requestedVideo)
      } else {
        const params = new URLSearchParams({})

        toast.error(t(`error.video_not_found`))

        history.push({
          search: params.toString(),
          state: {
            errorMessage: 'Something went wrong',
          },
        })
      }
    }
    loadVideoModal()
  }, [history, isAuthenticated, openVideoModal, videos, location.search])

  const handleSort = async (orderBy) => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.videosPage,
      label: `Videos Page: Sort - ${orderBy.field} ${orderBy.order}`,
    })

    const nextQueryParams = {
      ...queryParamsState,
      //page: 1,
      orderBy: orderBy.field,
      order: orderBy.order,
    }

    setShowSpinner(true)
    setQueryParamsState(nextQueryParams)
    const newVideos = await fetchVideos(nextQueryParams, videos)
    updateVideos(newVideos)
    setShowSpinner(false)
  }

  useEffect(() => {
    let isMounted = true

    const run = async () => {
      setShowSpinner(true)
      const { category, model, ...queryParams } = queryParamsState
      const nextQueryParams = {
        ...queryParams,
        ...(selectedModel ? { model: selectedModel } : {}),
        ...(selectedCategory ? { category: selectedCategory } : {}),
        // ...(selectedFilterLanguage ? { language_code: selectedFilterLanguage } : {}),
      }
      setQueryParamsState(nextQueryParams)
      const newVideos = await fetchVideos(nextQueryParams, videos)
      if (!isMounted) return
      updateVideos(newVideos)
      setShowSpinner(false)
    }

    run()
    return () => {
      isMounted = false
    }
  }, [fetchVideos, selectedCategory, selectedModel])

  const handleSearch = async (searchTerm) => {
    setShowSpinner(true)
    const isSearchValid = searchTerm.length > 2
    if (isSearchValid || isSearchDirty) {
      const { query, ...queryParams } = queryParamsState
      const nextQueryParams = {
        ...queryParams,
        ...(searchTerm.length > 2 && { query: searchTerm }),
        //page: 1,
      }
      setQueryParamsState(nextQueryParams)
      const newVideos = await fetchVideos(nextQueryParams, videos)
      updateVideos(newVideos)

      if (isSearchValid) {
        setIsSearchDirty(true)
      }
    }

    setShowSpinner(false)
  }

  const debouncedHandleSearch = useCallback(debounce(handleSearch, 200), [handleSearch])

  const handleLoadMore = async () => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.videosPage,
      label: 'Videos Page: Load More Videos',
    })

    // setShowSpinner(true)
    // const nextQueryParams = {
    //   ...queryParamsState,
    //   page: queryParamsState.page + 1,
    // }
    // setQueryParamsState(nextQueryParams)
    // const newVideos = await fetchVideos(nextQueryParams, videos)
    // setVideos(newVideos)
    // setShowSpinner(false)
    setCurrentPage((prev) => prev + 1)
  }

  const handleResetFilters = async () => {
    const { model, category, ...queryParams } = queryParamsState
    const nextQueryParams = {
      ...queryParams,
      //page: 1
    }
    setQueryParamsState(nextQueryParams)

    setSelectedModel(null)
    setSelectedCategory(null)
    setSelectedFilterLanguages([])
  }

  const handleFavourites = async (favourite_id, video_id, is_favourite) => {
    if (showSpinner || !checkPermission(props.userPermissions, videosWritePermission)) {
      return
    }

    const actionType = is_favourite ? 'Remove from Favorites' : 'Add to Favorites'
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.videosPage,
      label: `Videos Page: ${actionType}`,
    })

    setShowSpinner(true)

    if (favourite_id) {
      let isFav
      if (props.isFavourite) {
        isFav = false
      } else {
        isFav = is_favourite ? false : true
      }

      try {
        await putVideoFavourites(userId, favourite_id, isFav)

        if (props.isFavourite) {
          const updatedVideos = videos.filter((video) => video.id !== video_id)
          setVideos(updatedVideos)

          return
        }

        const updatedVideos = videos.map((video) => {
          if (video.id === video_id) {
            return { ...video, is_favourite: isFav }
          }
          return video
        })
        setVideos(updatedVideos)
      } catch (e) {
        console.error(e.message)
      } finally {
        setShowSpinner(false)
      }

      return
    }

    try {
      const favouriteData = await postVideoFavourites(userId, video_id, true)

      const updatedVideos = videos.map((video) => {
        if (video.id === video_id) {
          return { ...video, favourite_id: favouriteData?.data?.insert_id || 0, is_favourite: true }
        }
        return video
      })

      setVideos(updatedVideos)
    } catch (e) {
      console.error(e.message)
    } finally {
      setShowSpinner(false)
    }
  }

  const getDesktopView = () => {
    return (
      <>
        {videosTableData.length > 0 && (
          <GenericTable
            data={videosTableData.slice(0, pageSize * currentPage)}
            headers={[
              {
                title: t('title'),
                orderKey: 'title',
              },
              {
                title: t('model'),
                orderKey: 'models',
              },
              {
                title: t('category'),
                orderKey: 'category',
              },
              {
                title: '',
              },
            ]}
            keys={['title', 'model', 'category', 'action']}
            keyRenderer={{
              title: (item) => {
                return (
                  <div className="title-wrapper">
                    <div className="thumbnail-mask">
                      <div className="thumbnail-icon">
                        <VideoPlayIcon />
                      </div>
                      <img className="thumbnail" src={item.thumbnailUrl} alt={item.title} />
                    </div>
                    <div className="title">{item.title}</div>
                  </div>
                )
              },
              action: (item) => {
                return (
                  <div className="table-actions-wrapper">
                    {/* || props.isFavourite */}
                    {item.isFavourite ? (
                      <FilledFavIcon
                        className="star-icon filled"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFavourites(item.favouriteId, item.identifier, item.isFavourite)
                        }}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => {
                          if (e.key === keyboardEvents.ENTER) {
                            e.stopPropagation()
                            handleFavourites(item.favouriteId, item.identifier, item.isFavourite)
                          }
                        }}
                      />
                    ) : (
                      <UnFilledFavIcon
                        className="star-icon"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFavourites(item.favouriteId, item.identifier, item.isFavourite)
                        }}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => {
                          if (e.key === keyboardEvents.ENTER) {
                            e.stopPropagation()
                            handleFavourites(item.favouriteId, item.identifier, item.isFavourite)
                          }
                        }}
                      />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(video) => openVideoModal(video)}
            activeSort={{ field: queryParamsState.orderBy, order: queryParamsState.order }}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
      </>
    )
  }

  const getMobileView = () => {
    return (
      <>
        {videosTableData.slice(0, pageSize * currentPage).map((video, index) => (
          <div
            key={index}
            className={'card'}
            onClick={() => {
              if (expandedVideo !== index) {
                trackEvent({
                  action: gaActions.featureClick,
                  category: gaCategories.videosPage,
                  label: `Videos Page: Mobile Card Expanded - ${video.title}`,
                })
              }
              setExpandedVideo(index)
            }}
          >
            <div className={'card-item' + (expandedVideo !== index ? ' align-center' : '')}>
              <div
                className="card-item-title video-icon-title"
                onClick={(e) => {
                  e.stopPropagation()
                  openVideoModal(video)
                }}
              >
                <div className="thumbnail-mask">
                  <div className="thumbnail-icon">
                    <VideoPlayIcon />
                  </div>
                  <img className="thumbnail" src={video.thumbnailUrl} alt={video.title} />
                </div>
              </div>
              <div className={'card-item-body' + (expandedVideo !== index ? ' align-center' : '')}>
                <div>{video.title}</div>
                <div className="card-actions">
                  {video.isFavourite || props.isFavourite ? (
                    <FilledFavIcon
                      className="star-icon filled"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleFavourites(video.favouriteId, video.identifier, video.isFavourite)
                      }}
                    />
                  ) : (
                    <UnFilledFavIcon
                      className="star-icon"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleFavourites(video.favouriteId, video.identifier, video.isFavourite)
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
            {expandedVideo === index && (
              <>
                <div className="card-item">
                  <div className="card-item-title">{t('model')}</div>
                  <div className="card-item-body">{video.model}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('category')}</div>
                  <div className="card-item-body">{video.category}</div>
                </div>
              </>
            )}
          </div>
        ))}
      </>
    )
  }

  return (
    <>
      {!props.hideFilters && (
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={debouncedHandleSearch}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={selectedModel || selectedCategory || selectedFilterLanguages.length > 0}
            counterBadge={(selectedModel && 1) + (selectedCategory && 1) + selectedFilterLanguages.length}
          />
        </div>
      )}
      {!props.hideDisclaimer && (
        <div className="disclaimer-box-text">
          <div className="icon-wrapper">
            <InformationIcon />
          </div>
          <div className="disclaimer-wrapper">
            <div className="title">{t('video_context.disclaimer')}</div>
          </div>
        </div>
      )}
      {!isMobile && getDesktopView()}
      {isMobile && getMobileView()}
      {!showSpinner && !isFirstLoad && videosTableData.length === 0 && (
        <EmptyPageResults className={'empty-results-margin'} title={t('video_context.no_videos_found')} subtitle={t('cannot_find_matching_search')} />
      )}
      {videosTableData.length > pageSize * currentPage && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.singleSelect,
              dataset: models,
              input: selectedModel,
              output: (newModel) => {
                setSelectedModel(newModel)
              },
            },
            {
              name: t('category'),
              type: FilterType.singleSelect,
              dataset: categoryLabels,
              input: selectedCategory ? formatSnakeCase(selectedCategory) : null,
              output: (newCategory) => {
                setSelectedCategory(getApiValueFromLabel(videoCategories, newCategory))
              },
            },
            {
              name: t('language'),
              type: FilterType.multiSelect,
              dataset: languages,
              input: selectedFilterLanguages.map((lang) => languages.find((l) => l.toLowerCase() === `${lang.toLowerCase()}-lang`)).filter(Boolean),
              output: (filterLanguages) => {
                const langs = (filterLanguages || []).map((l) => l.split('-')[0]?.toLowerCase()).filter(Boolean)
                setSelectedFilterLanguages(langs)
              },
            },
          ]}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {selectedVideo && (
        <VideoModal
          title={selectedVideo.title}
          videoId={selectedVideo.video_id}
          onCloseVideo={closeVideoModal}
          gaAction={gaActions.videoClick}
          gaCategory={gaCategories.videosPage}
        />
      )}
    </>
  )
}

function mapStateToProps({ authedUser, userPermissions, productTypes, queryParams, videos }) {
  return {
    authedUser,
    videosLanguages: videos?.languages || [],
    productTypes: productTypes || {},
    userPermissions,
    queryParams,
    videoModels: videos?.models || [],
    videoCategories: videos?.categories || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadVideoModels,
        loadVideoCategories,
        loadVideoLanguages,
        handleSetQueryParams,
      },
      dispatch,
    ),
  }
}

export default connect(mapStateToProps, dispatchToProps)(VideosTable)

